import { useEffect, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Button from "@/v2-backoffice-client/components/button"
import { useUptimeSetMuteMutation } from "@/v2-backoffice-client/uptime/mute/setMute/uptime.setMute.mutation"
import {
  formatRelativeTime,
  getCategoryLabel,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type UptimeMuteDialogProps = DialogProps & {
  target: UptimeTargetFragment
}

// UptimeMuteDialog asks the operator for a free-text reason before
// setting (or refreshing) a target's mute. The reason is rendered in
// the muted badge so future operators always have context for why
// this incident was silenced -- "scheduled maintenance until Tuesday"
// vs "service is dead, no fix planned" should look different in the
// board view.
function UptimeMuteDialog(props: UptimeMuteDialogProps) {
  const { target, open, onOpenChange, ...dialogProps } = props

  const [reason, setReason] = useState(target.MutedReason ?? "")
  const [setMute, { loading, error, reset }] = useUptimeSetMuteMutation({
    onCompleted() {
      onOpenChange?.(false)
    },
  })

  // Re-prime the textarea every time the dialog reopens so an operator
  // doesn't see leftover text from the previous target. Also reset
  // the mutation error so the inline alert clears with the dialog.
  useEffect(() => {
    if (open) {
      setReason(target.MutedReason ?? "")
      reset()
    }
  }, [open, target.MutedReason, reset])

  const isUpdate = target.IsMuted

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {isUpdate ? "Update mute reason" : "Mute target"}
          </Dialog.Title>
          <Dialog.Description>
            {target.Name ?? target.Identifier} ·{" "}
            {getCategoryLabel(target.Source)}
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-3" error={error} />

        <form
          id="uptime-mute-form"
          className="space-y-4 py-2"
          onSubmit={(e) => {
            e.preventDefault()
            setMute({
              variables: {
                input: {
                  source: target.Source,
                  identifier: target.Identifier,
                },
                reason: reason.trim() || null,
              },
            })
          }}
        >
          <p className="text-sm text-muted-foreground">
            Muted targets are excluded from KPIs and the Active incidents view.
            They still get probed and continue to record history, so you can
            audit the underlying availability later.
          </p>

          <div className="space-y-2">
            <Label htmlFor="uptime-mute-reason">Reason (optional)</Label>
            <Textarea
              id="uptime-mute-reason"
              autoFocus
              rows={3}
              placeholder="e.g. scheduled maintenance until Tuesday"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
              }}
            />
          </div>

          {isUpdate && target.MutedAt && (
            <p className="text-xs text-muted-foreground">
              Originally muted {formatRelativeTime(target.MutedAt)}
              {target.MutedByName ? ` by ${target.MutedByName}` : ""}.
            </p>
          )}
        </form>

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={loading} form="uptime-mute-form">
            {isUpdate ? "Save reason" : "Mute"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default UptimeMuteDialog
